import { useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Table, { Badge, TableLink } from '../components/ui/Table';
import Modal from '../components/ui/Modal';
import Toast from '../components/ui/Toast';
import { fetchWithAdminAuth } from '../utils/adminAuth';

interface SiteText {
  id: string;
  key: string;
  description: string | null;
  value: string;
}

interface Banner {
  id: string;
  position: string;
  name: string;
  imageUrl: string | null;
  linkUrl: string | null;
  state: string;
  order: number;
}

export default function Banners() {
  const [siteTexts, setSiteTexts] = useState<SiteText[]>([]);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  // Text modal state
  const [isTextModalOpen, setIsTextModalOpen] = useState(false);
  const [editingText, setEditingText] = useState<SiteText | null>(null);
  const [textForm, setTextForm] = useState({ key: '', description: '', value: '' });
  const [savingText, setSavingText] = useState(false);
  
  // Banner modal state
  const [isBannerModalOpen, setIsBannerModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [bannerForm, setBannerForm] = useState({ 
    position: 'home', 
    name: '', 
    imageUrl: '', 
    linkUrl: '', 
    state: 'active',
    order: 0
  });
  const [savingBanner, setSavingBanner] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [textsRes, bannersRes] = await Promise.all([
        fetchWithAdminAuth('/api/admin/site-texts'),
        fetchWithAdminAuth('/api/admin/banners')
      ]);
      
      if (textsRes.ok) {
        const data = await textsRes.json();
        setSiteTexts(Array.isArray(data) ? data : []);
      }
      
      if (bannersRes.ok) {
        const data = await bannersRes.json();
        setBanners(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setToast({ message: 'Erro ao carregar dados', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Site Text handlers
  const handleEditText = (text: SiteText) => {
    setEditingText(text);
    setTextForm({ key: text.key, description: text.description || '', value: text.value });
    setIsTextModalOpen(true);
  };

  const handleNewText = () => {
    setEditingText(null);
    setTextForm({ key: '', description: '', value: '' });
    setIsTextModalOpen(true);
  };

  const handleSaveText = async () => {
    try {
      setSavingText(true);
      const response = await fetchWithAdminAuth('/api/admin/site-texts', {
        method: 'POST',
        body: JSON.stringify(textForm)
      });
      
      if (response.ok) {
        setToast({ message: 'Texto guardado com sucesso!', type: 'success' });
        setIsTextModalOpen(false);
        fetchData();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      setToast({ message: 'Erro ao guardar texto', type: 'error' });
    } finally {
      setSavingText(false);
    }
  };

  const handleDeleteText = async (key: string) => {
    if (!confirm('Tem certeza que deseja eliminar este texto?')) return;
    
    try {
      const response = await fetchWithAdminAuth(`/api/admin/site-texts/${key}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setToast({ message: 'Texto eliminado com sucesso!', type: 'success' });
        fetchData();
      }
    } catch (error) {
      setToast({ message: 'Erro ao eliminar texto', type: 'error' });
    }
  };

  // Banner handlers
  const handleEditBanner = (banner: Banner) => {
    setEditingBanner(banner);
    setBannerForm({
      position: banner.position,
      name: banner.name,
      imageUrl: banner.imageUrl || '',
      linkUrl: banner.linkUrl || '',
      state: banner.state,
      order: banner.order
    });
    setIsBannerModalOpen(true);
  };

  const handleNewBanner = () => {
    setEditingBanner(null);
    setBannerForm({ position: 'home', name: '', imageUrl: '', linkUrl: '', state: 'active', order: 0 });
    setIsBannerModalOpen(true);
  };

  const handleSaveBanner = async () => {
    try {
      setSavingBanner(true);
      const url = editingBanner 
        ? `/api/admin/banners/${editingBanner.id}`
        : '/api/admin/banners';
      const method = editingBanner ? 'PUT' : 'POST';
      
      const response = await fetchWithAdminAuth(url, {
        method,
        body: JSON.stringify(bannerForm)
      });
      
      if (response.ok) {
        setToast({ message: 'Banner guardado com sucesso!', type: 'success' });
        setIsBannerModalOpen(false);
        fetchData();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      setToast({ message: 'Erro ao guardar banner', type: 'error' });
    } finally {
      setSavingBanner(false);
    }
  };

  const handleDeleteBanner = async (id: string) => {
    if (!confirm('Tem certeza que deseja eliminar este banner?')) return;
    
    try {
      const response = await fetchWithAdminAuth(`/api/admin/banners/${id}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setToast({ message: 'Banner eliminado com sucesso!', type: 'success' });
        fetchData();
      }
    } catch (error) {
      setToast({ message: 'Erro ao eliminar banner', type: 'error' });
    }
  };

  const textosColumns = [
    { header: 'Chave', key: 'key' },
    { header: 'Descrição', key: 'description' },
    { 
      header: '', 
      key: 'actions', 
      render: (_val: any, row: SiteText) => (
        <div className="flex gap-2">
          <TableLink onClick={() => handleEditText(row)}>Editar</TableLink>
          <TableLink onClick={() => handleDeleteText(row.key)}>Eliminar</TableLink>
        </div>
      )
    }
  ];

  const bannersColumns = [
    { header: 'Posição', key: 'position' },
    { header: 'Nome', key: 'name' },
    { 
      header: 'Estado', 
      key: 'state', 
      render: (val: string) => (
        <Badge variant={val === 'active' ? 'success' : 'danger'}>
          {val === 'active' ? 'Ativo' : 'Inativo'}
        </Badge>
      )
    },
    { 
      header: '', 
      key: 'actions', 
      render: (_val: any, row: Banner) => (
        <div className="flex gap-2">
          <TableLink onClick={() => handleEditBanner(row)}>Editar</TableLink>
          <TableLink onClick={() => handleDeleteBanner(row.id)}>Eliminar</TableLink>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="px-3 sm:px-6 py-4 sm:py-5">
        <Card title="Banners & Textos do site" subtitle="A carregar...">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-5">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={true}
          onClose={() => setToast(null)}
        />
      )}

      <Card
        title="Banners & Textos do site"
        subtitle="Atualize textos como 'escolha um consultor', avisos de RGPD e mensagens de saldo."
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <h3 className="text-[0.85rem]">Textos principais</h3>
              <Button onClick={handleNewText}>+ Novo texto</Button>
            </div>
            <Table columns={textosColumns} data={siteTexts} />
          </div>
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <h3 className="text-[0.85rem]">Banners</h3>
              <Button onClick={handleNewBanner}>+ Novo banner</Button>
            </div>
            <Table columns={bannersColumns} data={banners} />
          </div>
        </div>
      </Card>

      {/* Text Modal */}
      <Modal
        isOpen={isTextModalOpen}
        onClose={() => setIsTextModalOpen(false)}
        title={editingText ? 'Editar Texto' : 'Novo Texto'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-1">Chave</label>
            <input
              type="text"
              value={textForm.key}
              onChange={(e) => setTextForm({ ...textForm, key: e.target.value })}
              disabled={!!editingText}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white disabled:opacity-50"
              placeholder="ex: texto_escolha_consultor"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Descrição</label>
            <input
              type="text"
              value={textForm.description}
              onChange={(e) => setTextForm({ ...textForm, description: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              placeholder="Descrição do texto"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Valor</label>
            <textarea
              value={textForm.value}
              onChange={(e) => setTextForm({ ...textForm, value: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white min-h-[100px]"
              placeholder="Conteúdo do texto"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setIsTextModalOpen(false)}>Cancelar</Button>
            <Button variant="primary" onClick={handleSaveText} disabled={savingText}>
              {savingText ? 'A guardar...' : 'Guardar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Banner Modal */}
      <Modal
        isOpen={isBannerModalOpen}
        onClose={() => setIsBannerModalOpen(false)}
        title={editingBanner ? 'Editar Banner' : 'Novo Banner'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-1">Posição</label>
            <select
              value={bannerForm.position}
              onChange={(e) => setBannerForm({ ...bannerForm, position: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
            >
              <option value="home">Home</option>
              <option value="chat">Chat</option>
              <option value="dashboard">Dashboard</option>
              <option value="payment">Pagamento</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Nome</label>
            <input
              type="text"
              value={bannerForm.name}
              onChange={(e) => setBannerForm({ ...bannerForm, name: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              placeholder="Nome do banner"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">URL da Imagem</label>
            <input
              type="text"
              value={bannerForm.imageUrl}
              onChange={(e) => setBannerForm({ ...bannerForm, imageUrl: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              placeholder="https://..."
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">URL de destino</label>
            <input
              type="text"
              value={bannerForm.linkUrl}
              onChange={(e) => setBannerForm({ ...bannerForm, linkUrl: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              placeholder="https://..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-300 mb-1">Estado</label>
              <select
                value={bannerForm.state}
                onChange={(e) => setBannerForm({ ...bannerForm, state: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              >
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Ordem</label>
              <input
                type="number"
                value={bannerForm.order}
                onChange={(e) => setBannerForm({ ...bannerForm, order: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setIsBannerModalOpen(false)}>Cancelar</Button>
            <Button variant="primary" onClick={handleSaveBanner} disabled={savingBanner}>
              {savingBanner ? 'A guardar...' : 'Guardar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
